import {Component, HostListener, Inject, OnDestroy, OnInit} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { navigation } from 'app/navigation/navigation';
import { locale as navigationEnglish } from 'app/navigation/i18n/en';
import { locale as navigationTurkish } from 'app/navigation/i18n/tr';
import {AccountService, UserService} from "./main/services";
import {Router} from "@angular/router";
import {NgIdleService} from "./main/services/ng-idel.service";
import {BrowserService} from "./main/services/browser.service";

@Component({
    selector   : 'app',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy
{
    fuseConfig: any;
    navigation: any;
    codeScanner = [];
    loading = false;
    lastKeyup: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private accountService: AccountService,
        private router: Router,
        private userService: UserService,
        private ngIdle: NgIdleService,
        private browserService: BrowserService,
    )
    {
        browserService.startBrowser();

        // Get default navigation
        this.navigation = navigation;

        // Register the navigation to the service
        this._fuseNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');

        // Add languages
        this._translateService.addLangs(['en', 'pl']);

        // Set the default language
        this._translateService.setDefaultLang('pl');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish, navigationTurkish);

        // Use a language
        this._translateService.use('pl');

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if ( this._platform.ANDROID || this._platform.IOS )
        {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if ( this.fuseConfig.layout.width === 'boxed' )
                {
                    this.document.body.classList.add('boxed');
                }
                else
                {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for ( let i = 0; i < this.document.body.classList.length; i++ )
                {
                    const className = this.document.body.classList[i];

                    if ( className.startsWith('theme-') )
                    {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });

        this.accountService.updateNavigationForCurrentUser();
    }

    // @HostListener('document:keypress', ['$event'])
    // handleKeyboardEvent(event: KeyboardEvent) {
    //     if (
    //         !this.accountService.hasRole('ROLE_SUPER_ADMIN') &&
    //         !this.accountService.hasRole('ROLE_ADMIN') &&
    //         !this.accountService.hasRole('ROLE_MANAGER') &&
    //         !this.accountService.hasRole('ROLE_RECEPTIONIST')
    //     ) {
    //         return event.key;
    //     }
    //     if (event.key != 'Enter' && event.key != ' ') {
    //         if (typeof this.lastKeyup !== "undefined" && ((new Date()).getTime() - this.lastKeyup)>250) {
    //             this.codeScanner = [];
    //         }
    //
    //         this.codeScanner.push(event.key);
    //         this.lastKeyup = (new Date()).getTime();
    //     } else if (event.key == 'Enter' && this.codeScanner.length > 4) {
    //         this.loading = true;
    //
    //         let code = this.codeScanner.join('');
    //
    //         this.userService.getByCardNumber(this.codeScanner.join('')).pipe(first())
    //             .subscribe(
    //                 data => {
    //                     this.router.navigate(['/users/edit/' + data.id]);
    //                 },
    //                 error => {
    //                     this.loading = false;
    //                     if (error == 'User not found.') {
    //                         console.log('/users/' + this.codeScanner.join('') + '/new');
    //                         this.router.navigate(['/users/' + code + '/new']);
    //                     }
    //                 });
    //
    //         this.codeScanner = [];
    //     }
    //
    //     return event.key;
    // }

    @HostListener('mouseenter')
    mouseenter() {
        this.accountService.refreshActivityStamp();
        if (this.accountService.isLoggedIn) {
            this.ngIdle.reset();
        }
    }

    @HostListener('mouseover')
    mouseover() {
        this.accountService.refreshActivityStamp();
        if (this.accountService.isLoggedIn) {
            this.ngIdle.reset();
        }
    }

    @HostListener('mouseout')
    mouseout() {
        this.accountService.refreshActivityStamp();
        if (this.accountService.isLoggedIn) {
            this.ngIdle.reset();
        }
    }

    @HostListener('click')
    click() {
        this.accountService.refreshActivityStamp();
        if (this.accountService.isLoggedIn) {
            this.ngIdle.reset();
        }

        this.browserService.checkIfNotToMuchOpenedTabs();
    }

    @HostListener('window:unload', [ '$event' ])
    unloadHandler(event) {
        this.browserService.closeBrowser();
    }

    @HostListener('window:beforeunload', [ '$event' ])
    beforeUnloadHandler(event) {
        this.browserService.beforeCloseBrowser();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void
    {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }
}
